/**
 * O que faz: Card de tempo médio de resposta com visões (geral/humano/IA/comercial) e distribuição por faixas.
 * Onde usa: Dashboard principal, ao lado do grid de métricas.
 * Conexões: Consome tempoMedioResposta do hook de dashboard.
 * Tabelas: Não acessa banco; consome métricas agregadas.
 * Data: 11/06/2026
 */

import { useState } from 'react';
import { Clock, TrendingUp, TrendingDown, Users, Bot, Calendar } from 'lucide-react';
import { DashboardMetrics } from '@/hooks/dashboard/types';

interface ResponseTimeCardProps {
  tempoMedioResposta: NonNullable<DashboardMetrics['tempoMedioResposta']>;
}

type ViewType = 'geral' | 'humano' | 'ia' | 'comercial';

const viewOptions = [
  { value: 'geral' as ViewType, label: 'Geral', icon: Clock },
  { value: 'humano' as ViewType, label: 'Humano', icon: Users },
  { value: 'ia' as ViewType, label: 'IA', icon: Bot },
  { value: 'comercial' as ViewType, label: 'Comercial', icon: Calendar },
];

export const ResponseTimeCard = ({ tempoMedioResposta }: ResponseTimeCardProps) => {
  const [selectedView, setSelectedView] = useState<ViewType>('humano');

  const getCurrentData = () => {
    switch (selectedView) {
      case 'humano':
        return { tempo: tempoMedioResposta.detalhes.tempoMedioHumanoFormatado, label: 'Tempo Humano' };
      case 'ia':
        return { tempo: tempoMedioResposta.detalhes.tempoMedioIAFormatado, label: 'Tempo IA' };
      case 'comercial':
        return { tempo: tempoMedioResposta.detalhes.tempoMedioComercialFormatado, label: 'Horário Comercial' };
      default:
        return { tempo: tempoMedioResposta.tempoMedioFormatado, label: 'Tempo Médio Geral' };
    }
  };

  const currentData = getCurrentData();
  const dist = tempoMedioResposta.detalhes.distribuicao;
  const total = dist.ate30min + dist.de30mina1h + dist.de1ha4h + dist.acimaDe4h;
  const getPct = (value: number) => (total > 0 ? (value / total) * 100 : 0);
  const variacaoDisponivel = tempoMedioResposta.variacaoDisponivel !== false;
  // Para tempo de resposta, variação positiva = piorou (resposta mais lenta)
  const piorou = tempoMedioResposta.variacao >= 0;

  return (
    <div className="flex h-full flex-col bg-card rounded-2xl border border-border p-4 sm:p-5 shadow-[0_1px_2px_rgba(19,41,27,0.05),0_3px_10px_rgba(19,41,27,0.05)]">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <span className="text-[13px] font-medium text-muted-foreground leading-snug">
          Tempo médio de resposta
        </span>
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent">
          <Clock className="h-4 w-4 text-accent-foreground" aria-hidden="true" />
        </div>
      </div>

      {/* Valor + variação */}
      <div className="mt-2 flex flex-wrap items-end justify-between gap-x-2 gap-y-1">
        <p className="lg-display text-2xl sm:text-[28px] font-bold tracking-tight tabular-nums text-foreground leading-none">
          {currentData.tempo}
        </p>
        {variacaoDisponivel ? (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums ${
              piorou ? 'bg-danger-subtle text-danger-text' : 'bg-success-subtle text-success-text'
            }`}
          >
            {piorou ? (
              <TrendingUp className="h-3 w-3" aria-hidden="true" />
            ) : (
              <TrendingDown className="h-3 w-3" aria-hidden="true" />
            )}
            {Math.abs(tempoMedioResposta.variacao)}%
          </span>
        ) : (
          <span className="text-[11px] font-medium text-muted-foreground">Sem comparação</span>
        )}
      </div>

      {/* Segmented control */}
      <div className="mt-4 rounded-full border border-border bg-muted/50 p-0.5">
        <div className="grid grid-cols-4 gap-0.5">
          {viewOptions.map((option) => {
            const Icon = option.icon;
            const isSelected = selectedView === option.value;
            return (
              <button
                key={option.value}
                onClick={() => setSelectedView(option.value)}
                className={`flex min-w-0 cursor-pointer items-center justify-center gap-1 rounded-full px-1.5 py-1.5 text-[11px] font-medium transition-colors duration-200 ${
                  isSelected
                    ? 'bg-card text-foreground shadow-sm ring-1 ring-border'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                title={option.label}
              >
                <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
                <span className="truncate">{option.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Distribuição por faixa de tempo */}
      <div className="mt-4 space-y-2.5">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Distribuição</p>
        {[
          { label: 'Até 30 min', val: dist.ate30min, color: 'bg-[#8fb83a]' },
          { label: '30 min – 1h', val: dist.de30mina1h, color: 'bg-[#5c8a1d]' },
          { label: '1h – 4h', val: dist.de1ha4h, color: 'bg-[#e5a100]' },
          { label: 'Mais de 4h', val: dist.acimaDe4h, color: 'bg-[#c93a32]' },
        ].map((item) => (
          <div key={item.label} className="space-y-1">
            <div className="flex justify-between text-[11px]">
              <span className="text-muted-foreground">{item.label}</span>
              <span className="font-semibold tabular-nums text-foreground">{item.val}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full ${item.color} rounded-full transition-all duration-500`}
                style={{ width: `${getPct(item.val)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
